/**
 * Iter254 — Mobile / tablet navigation drawer.
 *
 * The marketing site's MegaMenu is desktop-only (≥1024px). Below that we
 * collapse to a hamburger that opens a full-height drawer with a flat list
 * of the same top-level destinations. Nested mega-menu columns are flattened
 * into expandable accordions so visitors don't lose any links.
 *
 * Animation is CSS-only (no Motion / Framer) — slide-in from the right with
 * a backdrop fade. Esc + backdrop-click + nav-item click all dismiss.
 *
 * The drawer reads the same `MENU` data structure exported from MegaMenu.jsx
 * so we never drift between the desktop and mobile experiences.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown } from "lucide-react";
import { MENU, MENU_I18N, SOLO_LINKS } from "./MegaMenu";
import SocialRow from "./SocialRow";

export default function MobileNav() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const loc = useLocation();

  // Close drawer on every route change so navigation doesn't get stuck.
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Lock body scroll while open + Esc handler.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="mobile-nav-trigger"
        aria-label="Open navigation menu"
        className="lg:hidden qt-btn qt-btn-ghost"
        style={{ padding: "0.5rem", border: "1px solid var(--qt-border)" }}
      >
        <Menu size={18} />
      </button>

      {open && (
        <div
          data-testid="mobile-nav-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          style={{ position: "fixed", inset: 0, zIndex: 100 }}
        >
          {/* Backdrop */}
          <div
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-backdrop"
            style={{
              position: "absolute", inset: 0,
              background: "rgba(2, 6, 18, 0.92)",
              /* Iter255 — opaque backdrop; no see-through */
              animation: "qt-mobnav-fade 200ms ease-out",
            }}
          />
          {/* Drawer */}
          <aside
            data-testid="mobile-nav-panel"
            style={{
              position: "fixed", top: 0, right: 0,
              width: "min(92vw, 380px)",
              height: "100dvh",
              background: "#04091A",
              borderLeft: "1px solid var(--qt-cyan)",
              boxShadow: "-12px 0 32px -16px rgba(0, 229, 255, 0.35)",
              display: "flex", flexDirection: "column",
              zIndex: 1,
              animation: "qt-mobnav-slide 280ms cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "1rem 1.25rem",
                borderBottom: "1px solid var(--qt-border)",
              }}
            >
              <div className="qt-mono text-xs" style={{ letterSpacing: "0.18em", color: "var(--qt-cyan)" }}>
                NAVIGATION
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-close"
                aria-label="Close navigation menu"
                className="qt-btn qt-btn-ghost"
                style={{ padding: "0.35rem", border: "1px solid var(--qt-border)" }}
              >
                <X size={16} />
              </button>
            </div>

            <nav style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0.5rem 0" }}>
              {MENU.map((group) => {
                const isExp = expanded === group.key;
                const label = MENU_I18N[group.key] ? t(MENU_I18N[group.key], group.label) : group.label;
                return (
                  <div key={group.key} data-testid={`mobile-nav-section-${group.key}`}>
                    <button
                      type="button"
                      onClick={() => setExpanded(isExp ? null : group.key)}
                      aria-expanded={isExp}
                      style={{
                        width: "100%",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 8,
                        padding: "0.85rem 1.25rem",
                        background: "transparent",
                        border: "none",
                        borderTop: "1px solid var(--qt-border)",
                        color: "#fff",
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {label}
                        {group.badge && (
                          <span
                            className="qt-mono"
                            style={{
                              fontSize: 9, fontWeight: 700, letterSpacing: "0.14em",
                              padding: "2px 6px", borderRadius: 99,
                              background: `${group.badgeColor || "#1FDD83"}22`,
                              color: group.badgeColor || "#1FDD83",
                              border: `1px solid ${group.badgeColor || "#1FDD83"}55`,
                            }}
                          >{group.badge}</span>
                        )}
                      </span>
                      <ChevronDown
                        size={16}
                        style={{
                          transition: "transform 200ms",
                          transform: isExp ? "rotate(180deg)" : "rotate(0deg)",
                          color: "var(--qt-text-3)",
                        }}
                      />
                    </button>
                    {isExp && (
                      <div style={{ padding: "0.25rem 1.25rem 0.85rem", background: "rgba(0, 229, 255, 0.03)" }}>
                        {group.columns.map((col) => (
                          <div key={col.title} style={{ marginTop: "0.5rem" }}>
                            <div
                              className="qt-mono"
                              style={{
                                fontSize: 10, letterSpacing: "0.18em",
                                color: "var(--qt-text-3)", padding: "0.5rem 0 0.25rem",
                              }}
                            >
                              {col.title}
                            </div>
                            {col.items.map((it) => (
                              it.external ? (
                                <a
                                  key={it.to}
                                  href={it.to}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`mobile-nav-item-${it.title.toLowerCase().replace(/\s+/g, "-")}`}
                                  style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "0.55rem 0",
                                    color: "var(--qt-text-2)",
                                    fontSize: 13.5,
                                    textDecoration: "none",
                                  }}
                                >
                                  {it.title}
                                  {it.badge && (
                                    <span
                                      style={{
                                        fontSize: 8, fontWeight: 700, letterSpacing: "0.14em",
                                        padding: "1px 5px", borderRadius: 99,
                                        background: "rgba(31,221,131,0.16)",
                                        color: "#1FDD83",
                                        border: "1px solid rgba(31,221,131,0.4)",
                                      }}
                                    >{it.badge}</span>
                                  )}
                                </a>
                              ) : (
                                <Link
                                  key={it.to}
                                  to={it.to}
                                  data-testid={`mobile-nav-item-${it.title.toLowerCase().replace(/\s+/g, "-")}`}
                                  style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "0.55rem 0",
                                    color: "var(--qt-text-2)",
                                    fontSize: 13.5,
                                    textDecoration: "none",
                                  }}
                                >
                                  {it.title}
                                  {it.badge && (
                                    <span
                                      className="qt-mono"
                                      style={{
                                        fontSize: 8, fontWeight: 700, letterSpacing: "0.14em",
                                        padding: "1px 5px", borderRadius: 99,
                                        background: "rgba(31,221,131,0.16)",
                                        color: "#1FDD83",
                                        border: "1px solid rgba(31,221,131,0.4)",
                                      }}
                                    >{it.badge}</span>
                                  )}
                                </Link>
                              )
                            ))}
                          </div>
                        ))}
                        {group.cta && (
                          <Link
                            to={group.cta.to}
                            className="qt-btn qt-btn-secondary"
                            style={{
                              marginTop: "0.75rem",
                              fontSize: 13, padding: "0.55rem 0.875rem",
                              justifyContent: "center",
                            }}
                          >
                            {group.cta.label}
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {SOLO_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-solo-${l.label.toLowerCase()}`}
                  style={{
                    display: "block",
                    padding: "0.85rem 1.25rem",
                    borderTop: "1px solid var(--qt-border)",
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  {l.i18nKey ? t(l.i18nKey, l.label) : l.label}
                </Link>
              ))}

              {/* Iter275.4 — Pinned-bottom social row.
                  Mirrors the desktop footer placement so the brand
                  presence stays consistent everywhere a visitor lands.
                  Sits below the last solo link with a soft divider
                  and a small "FOLLOW" eyebrow so it doesn't read as
                  another navigation row. */}
              <div
                data-testid="mobile-nav-social-block"
                style={{
                  padding: "1.25rem 1.25rem 1.5rem",
                  borderTop: "1px solid var(--qt-border)",
                  marginTop: "auto",
                }}
              >
                <div
                  className="qt-mono"
                  style={{
                    fontSize: 10,
                    color: "var(--qt-text-3)",
                    letterSpacing: "0.18em",
                    fontWeight: 700,
                    marginBottom: 10,
                  }}
                >
                  FOLLOW QORTRACE
                </div>
                <SocialRow testidPrefix="mobile-nav" size={15} />
              </div>
            </nav>
          </aside>

          <style>{`
            @keyframes qt-mobnav-fade  { from { opacity: 0; } to { opacity: 1; } }
            @keyframes qt-mobnav-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
          `}</style>
        </div>
      )}
    </>
  );
}
